import { UiSurface } from "shared/ui/uiSurface"
import { Box, Paper, Typography } from "@mui/material"
import {
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps"
import {
  FC,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useDispatch } from "react-redux"
import {
  getFirstAndLastPositions,
  getMarkerByType,
} from "src/features/order/functions"
import { selectMe } from "src/features/profile"
import { SkeletonWrapper } from "src/features/shared-module/components"
import {
  appConstants,
  center,
  mapContainerStyle,
  MarkerTypes,
} from "src/features/shared-module/constants"
import { getProviderColor } from "src/features/shared-module/functions"
import {
  RouteSummary,
  RouteSummaryRaw,
} from "src/features/shared-module/models"
import SuperclusterLib from "supercluster"
import { useGetOrderPositionsQuery } from "../apis"
import { useAppSelector } from "../hooks"
import { useEmployeeColor } from "../hooks/useRouteDetail"
import type { CourierPosition } from "../slices/dashboard.slice"
import {
  courierPositionsSelector,
  haulerDetailMapSelector,
  setScrollingDetail,
} from "../slices/dashboard.slice"
import { ProviderMapLegend } from "./ProviderMapLegend"
const Supercluster = SuperclusterLib as unknown as typeof SuperclusterLib

interface OrderPositionData {
  id: number
  state: string
  haulerEmployeeId: number | null
  routeSummaryId: number | null
  latitude: number | null
  longitude: number | null
  reference: string
  sequence: number | null
}

export const OrderSummaryMapContainer: FC<{ date: string }> = memo(
  ({ date }) => {
    const { data: positions, isFetching: isPositionsFetching } =
      useGetOrderPositionsQuery(date)

    /* TODO: change later */
    const me: any = useAppSelector(selectMe)
    const defaultCenter = me?.user?.address || center

    // Only block on positions query (fast), not the full orders query (slow)
    if (isPositionsFetching && !positions) {
      return (
        <SkeletonWrapper
          height={mapContainerStyle.height}
          width={mapContainerStyle.width}
          sxProps={{
            borderRadius: "inherit",
          }}
        />
      )
    }
    return (
      <UiSurface id="UI-DSP-013">
        <GoogleMap
          mapId={appConstants.googleMapId}
          style={mapContainerStyle}
          defaultCenter={defaultCenter}
          disableDefaultUI={true}
          fullscreenControl={true}
          defaultZoom={3}
          maxZoom={20}
          minZoom={2}
          gestureHandling={"greedy"}
        >
          {positions && positions.length > 0 && (
            <MapBoundsFromPositions positions={positions} />
          )}
          <MapDirectionList />
          <ClusteredOrderMarkers positions={positions} />
          <ProviderMarkerList />
          <DriverMarkerList />
          <ProviderMapLegend />
        </GoogleMap>
      </UiSurface>
    )
  },
)

const ClusteredOrderMarkers: FC<{ positions?: OrderPositionData[] }> = memo(
  ({ positions }) => {
    const map = useMap()
    const dispatch = useDispatch()

    // Single Redux subscriptions at parent level instead of per-marker
    const orderAnimateId = useAppSelector(
      (state) => state.dashboard.orderAnimateId,
    )
    const employeeList = useAppSelector((state) => state.dashboard.employeeList)
    const employeeDetailVisibility = useAppSelector(
      (state) => state.dashboard.employeeDetailVisibility,
    )
    const orderMap = useAppSelector((state) => state.dashboard.orderMap)

    // Stable ref so click handlers don't cause re-renders
    const orderMapRef = useRef(orderMap)
    orderMapRef.current = orderMap

    // Build employee color lookup once
    const employeeColorMap = useMemo(() => {
      const m = new Map<number, string>()
      for (const e of employeeList) {
        m.set(e.id, e.color || "#666")
      }
      return m
    }, [employeeList])

    // Build supercluster index when positions change
    const clusterIndex = useMemo((): InstanceType<
      typeof Supercluster
    > | null => {
      if (!positions?.length) return null
      const sc = new Supercluster({
        radius: 60,
        maxZoom: 16,
      })
      sc.load(
        positions
          .filter((p) => p.latitude != null && p.longitude != null)
          .map((p) => ({
            type: "Feature" as const,
            properties: p,
            geometry: {
              type: "Point" as const,
              coordinates: [p.longitude!, p.latitude!],
            },
          })),
      )
      return sc
    }, [positions])

    // Recompute visible clusters on zoom/pan
    const [clusterData, setClusterData] = useState<any[]>([])

    const updateClusters = useCallback(() => {
      if (!map || !clusterIndex) {
        setClusterData([])
        return
      }
      const bounds = map.getBounds()
      const zoom = map.getZoom()
      if (!bounds || zoom == null) {
        setClusterData([])
        return
      }
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      setClusterData(
        clusterIndex.getClusters(
          [sw.lng(), sw.lat(), ne.lng(), ne.lat()],
          Math.floor(zoom),
        ),
      )
    }, [map, clusterIndex])

    useEffect(() => {
      if (!map) return
      const listener = map.addListener("idle", updateClusters)
      updateClusters()
      return () => listener.remove()
    }, [map, updateClusters])

    const handleMarkerClick = useCallback(
      (orderId: number) => {
        const order = orderMapRef.current[orderId]
        if (order) dispatch(setScrollingDetail(order))
      },
      [dispatch],
    )

    return (
      <>
        {clusterData.map((feature: any) => {
          const [lng, lat] = feature.geometry.coordinates
          const props = feature.properties

          // Cluster marker
          if (props.cluster) {
            return (
              <ClusterMarker
                key={`cluster-${props.cluster_id}`}
                position={{ lat, lng }}
                count={props.point_count}
                onClick={() => {
                  if (!clusterIndex) return
                  const expansionZoom = clusterIndex.getClusterExpansionZoom(
                    props.cluster_id,
                  )
                  map?.setZoom(Math.min(expansionZoom, 20))
                  map?.panTo({ lat, lng })
                }}
              />
            )
          }

          // Individual marker — visibility check
          const pos = props as OrderPositionData
          if (pos.haulerEmployeeId) {
            const detail = employeeDetailVisibility[pos.haulerEmployeeId]
            const key = pos.routeSummaryId || "individual"
            if (detail?.[key] === false) return null
          }

          const color = pos.haulerEmployeeId
            ? employeeColorMap.get(pos.haulerEmployeeId) || "#666"
            : "#666"

          let markerType = MarkerTypes.INDIVIDUAL_ORDER
          if (pos.routeSummaryId) markerType = MarkerTypes.ROUTE_ORDER
          else if (!pos.haulerEmployeeId)
            markerType = MarkerTypes.UNASSIGNED_ORDER

          return (
            <CommonMarker
              key={`order-marker-${pos.id}`}
              position={{ lat, lng }}
              icon={getMarkerByType(color, markerType, pos.sequence)}
              title={pos.reference}
              isActive={pos.id === orderAnimateId}
              isInitialAnimationRequired={true}
              onClick={() => handleMarkerClick(pos.id)}
            />
          )
        })}
      </>
    )
  },
)

const ClusterMarker: FC<{
  position: { lat: number; lng: number }
  count: number
  onClick: () => void
}> = memo(({ position, count, onClick }) => {
  const size = count < 10 ? 36 : count < 100 ? 44 : 52
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          backgroundColor: "#1A2A35",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: count >= 100 ? 12 : 14,
          border: "3px solid #fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          cursor: "pointer",
          transition: "transform 0.2s ease",
          "&:hover": {
            transform: "scale(1.15)",
          },
        }}
      >
        {count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count}
      </Box>
    </AdvancedMarker>
  )
})

const DriverMarkerList = memo(() => {
  const courierPositions = useAppSelector(courierPositionsSelector)
  const employeeList = useAppSelector((state) => state.dashboard.employeeList)

  return Object.values(courierPositions).map((pos: CourierPosition) => {
    const employee = employeeList.find((e) => e.id === pos.haulerEmployeeId)
    if (!employee) return null
    return (
      <DriverMarkerWithTrail
        key={`driver-${pos.haulerEmployeeId}`}
        position={pos}
        color={employee.color || "#666"}
        name={`${employee.firstName} ${employee.lastName}`}
      />
    )
  })
})

const DriverMarkerWithTrail: FC<{
  position: CourierPosition
  color: string
  name: string
}> = memo(({ position, color, name }) => {
  const map = useMap()
  const [showPopover, setShowPopover] = useState(false)
  const polylineRef = useRef<google.maps.Polyline | null>(null)

  // Draw trail from recent positions
  useEffect(() => {
    if (!map || !google || !position.recent?.length) {
      polylineRef.current?.setMap(null)
      polylineRef.current = null
      return
    }

    const path = [
      ...position.recent
        .slice()
        .reverse()
        .map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) }))
        .filter((p) => isFinite(p.lat) && isFinite(p.lng)),
      { lat: Number(position.latitude), lng: Number(position.longitude) },
    ].filter((p) => isFinite(p.lat) && isFinite(p.lng))

    if (path.length < 2) return

    if (polylineRef.current) {
      polylineRef.current.setPath(path)
    } else {
      polylineRef.current = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeWeight: 5,
        strokeOpacity: 0.6,
        map,
        icons: [
          {
            icon: { path: google.maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 2 },
            offset: "50%",
          },
        ],
      })
    }

    return () => {
      polylineRef.current?.setMap(null)
      polylineRef.current = null
    }
  }, [map, position.recent, position.latitude, position.longitude, color])

  const formatTime = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString()
    } catch {
      return ts
    }
  }

  return (
    <AdvancedMarker
      position={{ lat: position.latitude, lng: position.longitude }}
      title={name}
      onClick={() => setShowPopover((v) => !v)}
    >
      <Box sx={{ position: "relative" }}>
        {getMarkerByType(color, MarkerTypes.DRIVER)}
        {showPopover && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              mb: 1,
              p: 1.5,
              minWidth: 200,
              whiteSpace: "nowrap",
              zIndex: 10,
              borderTop: `3px solid ${color}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {name}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "2px 8px",
                fontSize: 12,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Lat/Lng
              </Typography>
              <Typography variant="caption">
                {position.latitude?.toFixed(5) ?? "—"},{" "}
                {position.longitude?.toFixed(5) ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Speed
              </Typography>
              <Typography variant="caption">
                {position.speed?.toFixed(1) ?? "—"} km/h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Altitude
              </Typography>
              <Typography variant="caption">
                {position.altitude?.toFixed(1) ?? "—"} m
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Heading
              </Typography>
              <Typography variant="caption">
                {position.heading?.toFixed(0) ?? "—"}°
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Odometer
              </Typography>
              <Typography variant="caption">
                {position.odometer?.toFixed(1) ?? "—"} km
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Battery
              </Typography>
              <Typography variant="caption">
                {position.batteryLevel != null
                  ? Math.round(position.batteryLevel * 100)
                  : "—"}
                %
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="caption">
                {formatTime(position.timestamp)}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </AdvancedMarker>
  )
})

const ProviderMarkerList = memo(() => {
  const haulerDetailMap = useAppSelector(haulerDetailMapSelector)

  const providerAnimateId = useAppSelector(
    (state) => state.dashboard.providerAnimateId,
  )

  return Object.entries(haulerDetailMap)
    .filter(([, hauler]) => hauler.address?.position)
    .map(([haulerId, hauler], index) => {
      return (
        <CommonMarker
          isInitialAnimationRequired={true}
          position={{
            lat: hauler.address?.position?.latitude ?? 0,
            lng: hauler.address?.position?.longitude ?? 0,
          }}
          icon={getMarkerByType(getProviderColor(index), MarkerTypes.PROVIDER)}
          title={hauler.name}
          isActive={providerAnimateId === hauler.id}
          key={haulerId}
        />
      )
    })
})

export const CommonMarker: FC<{
  position: {
    lat: any
    lng: any
  }
  icon?: ReactNode
  title: any
  isActive?: boolean
  isInitialAnimationRequired?: boolean
  onClick?: () => void
}> = memo(
  ({
    position,
    icon,
    title,
    onClick,
    isActive = false,
    isInitialAnimationRequired = false,
  }) => {
    const isInitialAnimationRequiredRef = useRef(isInitialAnimationRequired)
    const [isAnimation, setisAnimation] = useState(false)

    useEffect(() => {
      if (isInitialAnimationRequiredRef.current) return
      const timer = setTimeout(() => {
        setisAnimation(false)
      }, 2000)
      return () => clearTimeout(timer)
    }, [isInitialAnimationRequired])

    useEffect(() => {
      if (!isInitialAnimationRequiredRef.current) return
      setisAnimation(isActive)
    }, [isActive])

    return (
      <AdvancedMarker title={title} onClick={onClick} position={position}>
        <Box className={isAnimation ? "animate-bounce" : ""}>{icon}</Box>
      </AdvancedMarker>
    )
  },
)

const MapDirectionList: FC<any> = memo(() => {
  const orderRouteSummaryMap = useAppSelector(
    (state) => state.dashboard.orderRouteSummaryMap,
  )

  return Object.entries(orderRouteSummaryMap)
    .filter(([, objValue]) => objValue?.routeDetail)
    .map(([routeSummaryId, detail]) => (
      <RouteDirection
        key={routeSummaryId}
        route={detail.routeDetail as RouteSummary}
      />
    ))
})

const RouteDirection: FC<{ route: RouteSummary }> = ({ route }) => {
  const routeSummaryId = route.id
  const map = useMap()
  const polylinesRef = useRef<google.maps.Polyline[]>([])

  const summary = route.raw as RouteSummaryRaw

  const { firstStop, lastStop } = getFirstAndLastPositions(summary)

  const haulerEmployeeColor = useEmployeeColor(route.actorId)

  const isVisible = useAppSelector((state) => {
    const detail = state.dashboard.employeeDetailVisibility[route.actorId]
    return detail?.[routeSummaryId] ?? true
  })

  const clearPolylines = () => {
    polylinesRef.current.forEach((polyline) => polyline.setMap(null))
    polylinesRef.current = []
  }

  useEffect(() => {
    if (!map || !google || !haulerEmployeeColor || !route || !isVisible) return
    clearPolylines()

    const paths = JSON.parse(
      JSON.stringify(summary.routes.features[0].geometry.paths),
    )
    paths[0].unshift(...[[firstStop.geometry.x, firstStop.geometry.y]])
    paths[paths.length - 1].push(
      ...[[lastStop.geometry.x, lastStop.geometry.y]],
    )

    paths.forEach((path: any[]) => {
      const pathList = path.map(([lng, lat]: [number, number]) => {
        return {
          lat,
          lng,
        }
      })
      const polyline = new google.maps.Polyline({
        path: pathList,
        geodesic: true,
        strokeColor: haulerEmployeeColor,
        strokeWeight: 3,
        strokeOpacity: 1.0,
        map,
      })
      polylinesRef.current.push(polyline)
    })

    return () => {
      clearPolylines()
    }
  }, [
    map,
    route,
    haulerEmployeeColor,
    isVisible,
    summary.routes.features,
    firstStop.geometry,
    lastStop.geometry,
  ])

  return (
    google &&
    isVisible && (
      <>
        {[firstStop, lastStop].map((stop, index) => {
          return (
            <CommonMarker
              key={`circle-marker-${index}`}
              position={{
                lat: stop.geometry.y,
                lng: stop.geometry.x,
              }}
              icon={getMarkerByType(haulerEmployeeColor, MarkerTypes.CIRCLE)}
              title={JSON.stringify(stop.geometry)}
            />
          )
        })}
      </>
    )
  )
}

const MapBoundsFromPositions: FC<{ positions: OrderPositionData[] }> = memo(
  ({ positions }) => {
    const map = useMap()
    const haulerDetailMap = useAppSelector(haulerDetailMapSelector)

    useEffect(() => {
      if (!positions?.length || !map) return
      const bounds = new google.maps.LatLngBounds()
      positions.forEach((p) => {
        if (p.latitude == null || p.longitude == null) return
        bounds.extend(new google.maps.LatLng(p.latitude, p.longitude))
      })
      Object.values(haulerDetailMap).forEach((hauler) => {
        if (!hauler.address?.position) return
        bounds.extend(
          new google.maps.LatLng(
            hauler.address.position.latitude,
            hauler.address.position.longitude,
          ),
        )
      })
      map.fitBounds(bounds)
    }, [positions, haulerDetailMap, map])
    return null
  },
)
